import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReusableTable from './ReusableTable';
import en from '../../copy/en';

export default function WantsTable({ monthData, currency, onSave }) {
  const [wantsData, setWantsData] = useState(() => {
    // Initialize with real data if available, otherwise empty default
    if (monthData?.wants && monthData.wants.length > 0) {
      return monthData.wants;
    }
    return [{ category: '', amount: 0, id: 'default-wants-row-0' }];
  });
  const isUserOperatingRef = useRef(false);
  const previousMonthDataRef = useRef(null); // Initialize to null, not monthData
  const lastSaveDataRef = useRef(null);

  // Helper function to compare data arrays
  const areDataArraysEqual = useCallback((arr1, arr2) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    return arr1.every((item1, index) => {
      const item2 = arr2[index];
      return item1.id === item2.id && 
             item1.category === item2.category && 
             item1.amount === item2.amount;
    });
  }, []);

  // Load data from monthData
  useEffect(() => {
    // Skip if user is actively editing
    if (isUserOperatingRef.current) return;

    // Skip if monthData hasn't changed
    if (monthData === previousMonthDataRef.current) return;

    const hasWantsData = monthData?.wants && monthData.wants.length > 0;
    const isDifferentFromCurrent = !areDataArraysEqual(monthData?.wants, wantsData);

    if (hasWantsData && isDifferentFromCurrent) {
      // Update with real data from monthData
      setWantsData(monthData.wants);
    } else if (!hasWantsData && wantsData.length === 0) {
      // No data and no current data - set default row
      setWantsData([{ category: '', amount: 0, id: 'default-wants-row-0' }]);
    }

    // Update the previous monthData reference
    previousMonthDataRef.current = monthData;
  }, [monthData, wantsData, areDataArraysEqual]);

  const handleDataChange = (newData) => {
    // Set flag to prevent props from overriding local state
    isUserOperatingRef.current = true;
    
    setWantsData(newData);
    
    // Calculate total wants
    const totalWants = newData.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Save to backend
    if (onSave) {
      onSave({ 
        wants: newData,
        totalWants: totalWants
      });
    }
    
    // Update last save data reference
    lastSaveDataRef.current = newData;
    
    // Reset user operating flag after a short delay
    setTimeout(() => {
      isUserOperatingRef.current = false;
    }, 100);
  };

  // Calculate budget goal and determine label and amount
  const calculateBudgetStatus = () => {
    const totalIncome = monthData?.income ? monthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;

    const wantsGoalPercentage = monthData?.budgetGoals?.wantsPercentage || 30;
    const wantsGoalAmount = totalIncome * (wantsGoalPercentage / 100);

    const actualWantsAmount = wantsData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (actualWantsAmount > wantsGoalAmount) {
      return {
        label: "Overspent",
        amount: actualWantsAmount - wantsGoalAmount
      };
    } else if (actualWantsAmount === wantsGoalAmount) {
      return {
        label: "Total",
        amount: actualWantsAmount
      };
    } else {
      return {
        label: "Left to assign",
        amount: wantsGoalAmount - actualWantsAmount
      };
    }
  };

  const budgetStatus = calculateBudgetStatus();

  const tooltipContent = en.finance.tooltips.wants.description;

  return (
    <ReusableTable
      title="Wants"
      data={wantsData}
      onDataChange={handleDataChange}
      addRowLabel="Add want"
      currency={currency}
      showTotal={true}
      totalLabel={budgetStatus.label}
      customTotalAmount={budgetStatus.amount}
      tooltipContent={tooltipContent}
    />
  );
}
